import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useFormik } from "formik";
import { Camera } from "lucide-react";

import {
  Select,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectGroup,
  SelectItem,
  DatePicker,
  Switch,
  Button,
  ScrollArea,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Input,
  TaxIdInput,
  Label,
  MultiSelect,
} from "@/components/ui";

import { useFetchRulesQuery, usePermission, useToast, useUpdateUserMutation } from "@/hooks";
import { UpdateUserValidationSchema } from "@/shared";
import {
  DisplayError,
  formatAvatarName,
  formatDateForDatePicker,
  formatDateToBackend,
} from "@/utils";
import { User } from "@/types";

import { MessageError } from "../../message-error";
import { AlertDialogDelete } from "../../dialogs";

type FormUpdateUserProps = {
  handleClose: () => void;
  user: User;
};

export function FormUpdateUser({ handleClose, user }: FormUpdateUserProps) {
  const { t } = useTranslation();
  const { checkPermission } = usePermission();
  const { toast } = useToast();

  const { data: rolesData, isLoading: isLoadingRules } = useFetchRulesQuery();
  const { mutateAsync, isLoading } = useUpdateUserMutation();

  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    user.avatar || null
  );

  const formattedRoles = user.roles.map((role) => role.id).join(", ");

  const initialValues = {
    id: user.id,
    cpf: user.cpf || "",
    name: user.name || "",
    email: user.email || "",
    phone: user.phone || "",
    type: user.type || "",
    address: user.address || "",
    commission: user.commission || "",
    payment: user.payment || "",
    // treatment: user.treatment || "",
    roles: formattedRoles || "",
    crm: user.crm || "",
    date: formatDateForDatePicker(user.date || "") || "",
    avatar: user.avatar || "",
    status: user.status || 0,
    _method: "PUT",
  };

  const { handleSubmit, ...formik } = useFormik({
    initialValues,
    validationSchema: UpdateUserValidationSchema(),
    onSubmit: async (values: any) => {
      let formatValues;
      if (values.type === "admin") {
        formatValues = {
          ...values,
          roles: [],
        };
      } else {
        formatValues = {
          ...values,
          roles: Array.isArray(values.roles)
            ? values.roles
            : typeof values.roles === "string" && values.roles.trim().length > 0
              ? values.roles
                .split(",")

                // @ts-ignore
                .map((item) => item.trim())
                .filter((item: any) => item)
              : [],
        };
      }
      handleCreateUserSubmit(formatValues);
    },
  });

  async function handleCreateUserSubmit(request: any) {
    try {
      const fd = new FormData();
      fd.append("id", request.id || "");
      fd.append("name", request.name || "");
      fd.append("email", request.email || "");
      // fd.append("crm", request.crm || "");
      fd.append("cpf", request.cpf || "");
      fd.append("phone", request.phone || "");
      fd.append("type", request.type || "");
      if (Array.isArray(request.roles) && request.roles.length > 0) {
        request.roles.forEach((role: string | Blob, index: any) => {
          fd.append(`roles[${index}]`, role);
        });
      } else {
        fd.append("roles", request.roles || null);
      }
      // fd.append("treatment", request.treatment || "");
      fd.append("address", request.address || "");
      fd.append("date", formatDateToBackend(request.date) || "");
      fd.append("commission", request.commission || "");
      fd.append("payment", request.payment || "");
      fd.append("status", request.status || "");
      fd.append("active", request.status || "");
      fd.append("avatar", file || "");
      fd.append("_method", "PUT");
      await mutateAsync(
        { id: user.id, data: fd },
        {
          onSuccess: () => {
            handleClose();
            formik.resetForm();
            setPreviewUrl(null);
            toast({
              title: t("LABEL.success"),
              description: t("MESSAGE.updated successfully"),
            });
          },
          onError: (error) => {
            toast({
              title: t("LABEL.error"),
              description: DisplayError(error, t("MESSAGE.error_message")),
              variant: "destructive",
            });
          },
        }
      );
    } catch (error) {
      toast({
        title: t("LABEL.error"),
        description: DisplayError(error, t("MESSAGE.error_message")),
        variant: "destructive",
      });
    } finally {
      formik.setSubmitting(false);
    }
  }

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files && event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === "string") {
          setPreviewUrl(reader.result);
        }
      };
      reader.readAsDataURL(selectedFile);
    }
  };
  return (
    <form
      onSubmit={handleSubmit}
      className="max-h-[50vh] md:max-h-[70vh] 2xl:max-h-[90vh] flex flex-col gap-2"
    >
      <ScrollArea className=" h-full">
        <div className="space-y-2">
          <div className="max-w-28">
            <div className="relative">
              <Avatar className="rounded-xl size-28 relative">
                <AvatarImage src={previewUrl || ""} />
                <AvatarFallback>
                  {formatAvatarName(formik.values.name || "Avatar")}
                </AvatarFallback>
              </Avatar>
              <label
                htmlFor="avatar"
                className="cursor-pointer absolute right-0 -mr-2"
              >
                <Camera
                  size={32}
                  className="absolute bg-teal-700 p-1 rounded-[6px] text-white bottom-0 right-0"
                />
              </label>
              <input
                hidden
                id="avatar"
                type="file"
                accept=".png, .jpg"
                onBlur={formik.handleBlur}
                onFocus={formik.handleBlur}
                name="avatar"
                onChange={handleImageChange}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label required>{t("LABEL.name")}</Label>
              <Input
                isInvalid={formik.touched.name && !!formik.errors.name}
                isValid={formik.touched.name && !formik.errors.name}
                messageError={formik.errors.name as string}
                {...formik.getFieldProps("name")}
              />
            </div>
            <div className="space-y-1">
              <Label required>{t("LABEL.email")}</Label>
              <Input
                isInvalid={formik.touched.email && !!formik.errors.email}
                isValid={formik.touched.email && !formik.errors.email}
                messageError={formik.errors.email as string}
                {...formik.getFieldProps("email")}
              />
            </div>
            <div className="space-y-1">
              <Label required>{t("LABEL.cpf")}</Label>
              <TaxIdInput
                typeMask="cpf"
                isInvalid={formik.touched.cpf && !!formik.errors.cpf}
                isValid={formik.touched.cpf && !formik.errors.cpf}
                messageError={formik.errors.cpf as string}
                {...formik.getFieldProps("cpf")}
              />
            </div>
            {/* <div className="space-y-1">
              <Label required>{t("LABEL.registration number")}</Label>
              <Input
                isInvalid={formik.touched.crm && !!formik.errors.crm}
                isValid={formik.touched.crm && !formik.errors.crm}
                messageError={formik.errors.crm as string}
                {...formik.getFieldProps("crm")}
              />
            </div> */}
            <div className="space-y-1">
              <Label required>{t("LABEL.phone")}</Label>
              <Input
                inputPhone
                isInvalid={formik.touched.phone && !!formik.errors.phone}
                isValid={formik.touched.phone && !formik.errors.phone}
                messageError={formik.errors.phone as string}
                {...formik.getFieldProps("phone")}
              />
            </div>
            <div className="space-y-1">
              <Label required>{t("LABEL.account_type")}</Label>
              <div className="space-y-1">
                <Select
                  {...formik.getFieldProps("type")}
                  onValueChange={(value) => {
                    formik.setFieldValue("type", value);
                  }}
                >
                  <SelectTrigger
                    isInvalid={formik.touched.type && !!formik.errors.type}
                    isValid={formik.touched.type && !formik.errors.type}
                    className="disabled:opacity-100"
                  >
                    <SelectValue placeholder="Selecione o tipo de usuário" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="employ">
                        {t("LABEL.employee")}
                      </SelectItem>
                      <SelectItem value="admin">{t("LABEL.admin")}</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>

                <MessageError messageError={formik.errors.type as string} />
              </div>
            </div>

            <div className="space-y-1">
              <Label required>{t("LABEL.role")}</Label>
              <div className="space-y-1">
                <MultiSelect
                  options={
                    rolesData?.roles?.map((rule) => ({
                      value: rule.id,
                      label: t(rule.name),
                    })) || []
                  }
                  loading={isLoadingRules || !rolesData}
                  isInvalid={formik.touched.roles && !!formik.errors.roles}
                  isValid={formik.touched.roles && !formik.errors.roles}
                  placeholder="Selecione um cargo..."
                  emptyMessage="Nenhum cargo encontrado"
                  width="300px"
                  value={formik.values.roles}
                  onChange={(value) => {
                    formik.setFieldTouched("roles", true);
                    formik.setFieldValue("roles", value);
                  }}
                  onBlur={() => {
                    formik.setFieldTouched("roles", true);
                    formik.handleBlur("roles");
                  }}
                  messageError={formik.errors.roles as string}
                />
              </div>
            </div>
            {/* <div className="space-y-1">
              <Label required>{t("LABEL.form of treatment")}</Label>
              <div className="space-y-1">
                <Select
                  {...formik.getFieldProps("treatment")}
                  onValueChange={(value) => {
                    formik.setFieldTouched("treatment", true);
                    formik.setFieldValue("treatment", value);
                  }}
                >
                  <SelectTrigger
                    isInvalid={
                      formik.touched.treatment && !!formik.errors.treatment
                    }
                    isValid={
                      formik.touched.treatment && !formik.errors.treatment
                    }
                    className="disabled:opacity-100"
                  >
                    <SelectValue placeholder="selecionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="Sir">{t("LABEL.sir")}</SelectItem>
                      <SelectItem value="Madam">{t("LABEL.madam")}</SelectItem>
                      <SelectItem value="Doctor">
                        {t("LABEL.doctor")}
                      </SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <MessageError
                  messageError={formik.errors.treatment as string}
                />
              </div>
            </div> */}
            <div className="space-y-1">
              <Label>{t("LABEL.address")}</Label>
              <Input
                isInvalid={formik.touched.address && !!formik.errors.address}
                isValid={formik.touched.address && !formik.errors.address}
                messageError={formik.errors.address as string}
                {...formik.getFieldProps("address")}
              />
            </div>
            <div className="space-y-1">
              <Label>{t("LABEL.birthday")}</Label>
              <DatePicker
                {...formik.getFieldProps("date")}
                isInvalid={formik.touched.date && !!formik.errors.date}
                isValid={formik.touched.date && !formik.errors.date}
                messageError={formik.errors.date as string}
              />
            </div>
            <div className="space-y-1">
              <Label>{t("LABEL.payment details")}</Label>
              <Input
                isInvalid={formik.touched.payment && !!formik.errors.payment}
                isValid={formik.touched.payment && !formik.errors.payment}
                messageError={formik.errors.payment as string}
                {...formik.getFieldProps("payment")}
              />
            </div>
          </div>

        </div>
      </ScrollArea>
      <div className="flex items-center justify-end gap-x-6">
        <div className="flex items-center space-x-2">
          <Switch
            id="active"
            checked={formik.values.status === 1 ? true : false}
            onCheckedChange={(checked) =>
              formik.setFieldValue("status", checked ? 1 : 0)
            }
          />
          <Label htmlFor="active">Inativo / Ativo</Label>
        </div>
        <AlertDialogDelete
          endpoint={`/users/${user.id}`}
          queryKey="users"
          callBack={handleClose}
        >
          <Button
            type="button"
            className="px-10 bg-destructive hover:bg-destructive/80"
            disabled={!checkPermission("user", "delete user")}
          >
            {t("LABEL.delete")}
          </Button>
        </AlertDialogDelete>
        <Button
          type="submit"
          className="px-10"
          disabled={!checkPermission("user", "update user") || isLoading}
          isLoading={isLoading}
        >
          {t("LABEL.save")}
        </Button>
      </div>
    </form>
  );
}
